import type { Visit } from './visits'

/**
 * THE JOBS BOARD, AND WHY IT COULD NOT BE BUILT UNTIL NOW.
 *
 * JOB-01. lib/leads.ts has carried this sentence since the day it was written:
 * the Jobs board needs reservation, scheduling and crew events that do not
 * exist in this product yet, four of its six columns would be permanently
 * empty, so it is not shipped as a board pretending otherwise.
 *
 * Three of those four have shipped. A visit is a record, a visit can be
 * started and finished, and a visit can name who is going. So every column
 * below is a real event on a real job, and not one of them is a field.
 *
 * A COLUMN IS NOT A STATUS. Same rule as the pipeline: the card's column is
 * worked out from what happened, so it cannot disagree with the job, and
 * nothing on this board can be dragged. Dragging says set the column, and
 * there is no column to set. What each card offers instead is the next real
 * thing to do, which is the only way a card ever moves.
 *
 * WHAT IS STILL NOT A COLUMN. Reserved materials. MAT-03 is explicit that a
 * reservation is neither loaded nor installed stock, and there is still no
 * operation key to make one idempotent, so a Reserved column would be the
 * empty one this file exists to avoid.
 */

export type JobColumn =
  | 'unpriced'
  | 'awaiting'
  | 'ready'
  | 'scheduled'
  | 'onsite'
  | 'done'
  | 'lost'

export const JOB_COLUMNS: { id: JobColumn; label: string; blurb: string }[] = [
  { id: 'unpriced', label: 'Not priced', blurb: 'A house, and no number on it yet.' },
  { id: 'awaiting', label: 'Waiting on a yes', blurb: 'Priced, and nobody has agreed to it.' },
  { id: 'ready', label: 'Ready to book', blurb: 'Agreed, and no install on the calendar.' },
  { id: 'scheduled', label: 'Booked', blurb: 'On the calendar, nobody there yet.' },
  { id: 'onsite', label: 'On site', blurb: 'Somebody pressed start and has not finished.' },
  { id: 'done', label: 'Installed', blurb: 'The install was marked finished.' },
  { id: 'lost', label: 'Lost', blurb: 'Written off.' },
]

/** Everything the board reads. All of it already exists on a job. */
export interface BoardJob {
  id: string
  customer: string
  address: string
  name: string
  status: 'draft' | 'quoted' | 'won' | 'lost'
  /** True when a scope has been frozen. See lib/approval.ts. */
  scopeApproved: boolean
  /** Total on the paper. Zero when nothing is priced. */
  total: number
  balance: number
  visits: Visit[]
}

const installs = (j: BoardJob) => j.visits.filter((v) => v.kind === 'install')

/**
 * WHICH COLUMN, READ TOP DOWN, AND THE ORDER IS THE ARGUMENT.
 *
 * Lost is first, because a contractor who wrote a job off does not want it
 * back on the board because somebody left a visit on the calendar. After that
 * the newest true event wins: finished beats started beats booked beats
 * agreed, because that is the order they can only ever happen in.
 */
export function columnOf(j: BoardJob): JobColumn {
  if (j.status === 'lost') return 'lost'
  const inst = installs(j)
  if (inst.some((v) => v.doneAt)) return 'done'
  if (inst.some((v) => v.startedAt && !v.doneAt)) return 'onsite'
  if (inst.length > 0) return 'scheduled'
  if (j.scopeApproved || j.status === 'won') return 'ready'
  if (j.total > 0 || j.status === 'quoted') return 'awaiting'
  return 'unpriced'
}

/**
 * WHAT MOVES THIS CARD, said as the thing a person does rather than as a
 * column name. Nothing here sets a state: every one of these is an event
 * somewhere else in the product, and the column follows from it.
 */
export interface NextStep {
  /** The words on the button. */
  label: string
  /**
   * Where it goes. The board does not know how to do any of these itself.
   *
   * 'price' and 'scope' both open the job; they differ in which of its steps
   * they land on. They used to be one value, 'design', because Design & Quote
   * was a destination. It is a strip of tabs on the job now, so the two cases
   * that always meant different steps can say so.
   */
  goes: 'price' | 'scope' | 'schedule' | 'today' | 'job'

  /** What actually happens, for the card's second line. */
  does: string
}

export function nextStep(j: BoardJob): NextStep | null {
  switch (columnOf(j)) {
    case 'unpriced':
      return { label: 'Price it', goes: 'price', does: 'Write the quote on this house.' }
    case 'awaiting':
      return {
        label: 'Open the scope',
        goes: 'scope',

        does: 'Freeze what was agreed, and the card moves on its own.',
      }
    case 'ready':
      return { label: 'Book the install', goes: 'schedule', does: 'Put it on the week.' }
    case 'scheduled':
      return { label: 'See the week', goes: 'schedule', does: 'Starting the visit moves it.' }
    case 'onsite':
      return { label: "Open today's stops", goes: 'today', does: 'Finishing the visit moves it.' }
    case 'done':
      return j.balance > 0
        ? { label: 'Open the job', goes: 'job', does: 'The balance is still owed.' }
        : null
    default:
      return null
  }
}

export interface Column {
  id: JobColumn
  label: string
  blurb: string
  jobs: BoardJob[]
  /** What the column is worth. Zero on the ones where money is not the point. */
  value: number
}

export function board(jobs: BoardJob[]): Column[] {
  return JOB_COLUMNS.map((c) => {
    const mine = jobs.filter((j) => columnOf(j) === c.id)
    return {
      ...c,
      jobs: mine,
      value: mine.reduce((n, j) => n + j.total, 0),
    }
  })
}

/**
 * The board says what it is not, in the same breath as showing itself. Every
 * one of these is a thing a person would reasonably expect a kanban to do.
 */
export const NOT_ON_THE_BOARD: string[] = [
  'Dragging a card. A column here is worked out from what happened rather than stored, so there is nothing to set: cards move when somebody approves a scope, books a visit, or presses start.',
  'A Reserved column. A reservation is neither loaded nor installed stock and still has no operation key a retry could reuse, so it would be the empty column this board was held back to avoid.',
  'Who is behind. Nothing records how long a job is meant to take between one event and the next.',
]
